import * as React from 'react';
import {FormControl, FormLabel, Box, IconButton, FormHelperText} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import InsertInvocationIcon from '@mui/icons-material/InsertInvitation';
import {DateUtils} from "../../util/DateUtils";
import moment, { Moment } from 'moment';
import { useMemo, memo } from 'react';

interface DateProps {
    variant?: 'outlined' | 'filled' | 'standard';
    onChange: (date: Moment | null) => void;
    onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onKeyUp?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    label?: string;
    description?: string;
    required?: boolean;
    value: Moment | null;
    error?: string;
    format?: string;
    enabled?: boolean;
    visible?: boolean;
    appValidated?: boolean;
}

const Date = memo((props: DateProps) => {
    const {
        variant = 'outlined',
        onChange,
        onFocus,
        onKeyUp,
        label,
        description,
        required = false,
        value,
        error = '',
        format = DateUtils.getLongDateFormat(),
        enabled = true,
        visible = true,
        appValidated = false
    } = props;

    // Memoize error processing - only show errors after submission
    const { processedError, showError } = useMemo(() => {
        let processedError = error;

        // Only process errors if form has been appValidated
        if (!appValidated) {
            return { processedError: '', showError: false };
        }

        if (error?.includes('must be string')) {
            processedError = '';
        } else if (error?.includes('should match pattern')) {
            processedError = `Please ensure a valid date is entered in the ${DateUtils.getLongDateFormat()} date format`;
        } else if (error?.includes('should match format "date"')) {
            processedError = `Date should match the ${DateUtils.getLongDateFormat()} date format`;
        }

        // Add required field validation
        if (required && !value) {
            processedError = 'This field is required';
        }

        const showError = processedError !== '';
        return { processedError, showError };
    }, [error, value, required, appValidated]);

    // Memoize computed values
    const showDescription = useMemo(() =>
            description !== undefined && description !== "",
        [description]
    );

    // Memoize the formatted value
    const formattedValue = useMemo(() =>
            moment(value, format),
        [value, format]
    );

    // Memoize the openPickerButton component
    const OpenPickerButton = useMemo(() =>
            (buttonProps: any) => (
                <IconButton {...buttonProps}>
                    <InsertInvocationIcon color="disabled" />
                </IconButton>
            ),
        []
    );

    // Early return for hidden components
    if (!visible) {
        return null;
    }

    return (
        <Box sx={{ display: 'block' }}>
            <FormControl className="form-control" fullWidth={true}>
                <FormLabel required={required} color="secondary" className="form-separate-label">
                    {label}
                </FormLabel>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                        format={format}
                        value={formattedValue}
                        onChange={onChange}
                        disabled={!enabled}
                        slots={{
                            openPickerButton: OpenPickerButton
                        }}
                        slotProps={{
                            openPickerButton: {
                                style: { marginRight: 0 },
                            },
                            textField: {
                                required,
                                onFocus,
                                onKeyUp,
                                error: showError,
                                variant,
                                fullWidth: true,
                            },
                        }}
                    />
                </LocalizationProvider>
                {showDescription && (
                    <FormHelperText>
                        {description}
                    </FormHelperText>
                )}
                {showError && (
                    <FormHelperText error={true}>
                        {processedError}
                    </FormHelperText>
                )}
            </FormControl>
        </Box>
    );
});

Date.displayName = 'Date';

export default Date;
